import { motion } from 'framer-motion'
import { BarChart3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { reportsApi } from '@/services/api'
import { buildCategoryRows, type CategorySpendingRow } from './categoryUtils'
import { formatCurrency } from '@/lib/utils'

interface CategoryChartProps {
  period?: string
}

export function CategoryChart({ period = 'month' }: CategoryChartProps) {
  const { t } = useTranslation()
  const [categories, setCategories] = useState<CategorySpendingRow[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadCategoryData = async () => {
      setIsLoading(true)
      try {
        const response = await reportsApi.getSpendingByCategory({ period })
        const spendingData = response.data.data || response.data
        const categoryData = Array.isArray(spendingData) ? spendingData : []

        const categoryRows = buildCategoryRows(
          categoryData,
          {
            business: t('reports.categories.business'),
            transport: t('reports.categories.transport'),
            restaurants: t('reports.categories.restaurants'),
            retail: t('reports.categories.retail'),
            services: t('reports.categories.services'),
            tourism: t('reports.categories.tourism'),
          },
          {
            business: 'bg-primary',
            transport: 'bg-secondary',
            restaurants: 'bg-accent',
            retail: 'bg-success',
            services: 'bg-warning',
            tourism: 'bg-info',
          }
        )

        setCategories(categoryRows)
      } catch (error) {
        console.error('Error loading category chart data:', error)
        setCategories([])
      } finally {
        setIsLoading(false)
      }
    }

    loadCategoryData()
  }, [period, t])

  const maxAmount = useMemo(() => {
    return categories.reduce((max, category) => Math.max(max, category.amount), 0)
  }, [categories])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-4"
    >
      <h2 className="text-lg font-semibold text-text-primary mb-4">
        {t('reports.categoryChartTitle')}
      </h2>

      {isLoading ? (
        <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
          <p className="text-text-secondary">{t('common.loading')}</p>
        </div>
      ) : (
        <div className="h-48 rounded-lg bg-gray-50 px-4 py-3">
          <div className="flex h-full items-end justify-between gap-3">
            {categories.map((category) => {
              const height = maxAmount > 0 ? (category.amount / maxAmount) * 100 : 0
              return (
                <div key={category.id} className="flex h-full flex-1 flex-col items-center justify-end">
                  <div
                    className={`w-full rounded-lg ${category.color}`}
                    style={{ height: `${height}%` }}
                  />
                  <div className="mt-2 text-center text-xs text-text-secondary">
                    <div className="font-medium text-text-primary">{category.name}</div>
                    <div>{formatCurrency(category.amount, 'STN')}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {!isLoading && categories.every((category) => category.amount === 0) && (
        <div className="mt-4 flex items-center gap-2 text-sm text-text-secondary">
          <BarChart3 className="h-4 w-4" />
          {t('reports.noSpendingData')}
        </div>
      )}
    </motion.div>
  )
}
